import React, { useState, useEffect, useRef } from 'react';
import { useImageContext } from '../contexts/ImageContext';
import { useDiskColor } from '../contexts/DiskColorContext';
import { useAudio } from '../contexts/AudioContext';
import './css/EnhancedPreview.css';

const EnhancedPreview = ({ design = 'Design1', showControls = true }) => {
  const { currentImage } = useImageContext();
  const { diskColor } = useDiskColor();
  const { audioData, updatedRegion } = useAudio();
  const [rotation, setRotation] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);
  const checkTimeIntervalRef = useRef(null);

  // Handle rotation animation
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setRotation(prev => prev + 2);
        // Let rotation grow continuously - CSS handles large values smoothly
        // (e.g., rotate(720deg) = rotate(360deg) visually, but no glitch)
      }, 50);
    }
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isPlaying]);

  // Handle audio playback
  useEffect(() => {
    if (audioData?.file) {
      let objectUrl;
      let audio;
      try {
        // Create blob URL from the audio file
        objectUrl = URL.createObjectURL(audioData.file);
        audio = new Audio();
        audio.src = objectUrl;
        audio.preload = 'metadata';
        
        // Handle audio errors
        audio.addEventListener('error', (e) => {
          setAudioElement(null);
        });
        
        // Set audio element after it's loaded
        const handleLoadedMetadata = () => {
          if (updatedRegion && audio) {
            audio.currentTime = updatedRegion.start;
          }
          setAudioElement(audio);
        };
        
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);
        
        // Also try to load immediately
        audio.load();
        
        return () => {
          if (audio) {
            audio.pause();
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
            audio.removeEventListener('error', () => {});
            audio.src = ''; // Clear the source before revoking
          }
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
          }
          setAudioElement(null);
        };
      } catch (error) {
        setAudioElement(null);
      }
    } else {
      setAudioElement(null);
    }
  }, [audioData?.file, updatedRegion]);

  const togglePlayback = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!audioElement || !updatedRegion) {
      return;
    }

    // Clear any existing interval
    if (checkTimeIntervalRef.current) {
      clearInterval(checkTimeIntervalRef.current);
      checkTimeIntervalRef.current = null;
    }

    if (isPlaying) {
      audioElement.pause();
      setIsPlaying(false);
    } else {
      audioElement.currentTime = updatedRegion.start;
      audioElement.play().catch((error) => {
        setIsPlaying(false);
      });
      setIsPlaying(true);

      // Stop at region end
      checkTimeIntervalRef.current = setInterval(() => {
        if (audioElement && updatedRegion) {
          if (audioElement.currentTime >= updatedRegion.end) {
            audioElement.pause();
            audioElement.currentTime = updatedRegion.start;
            setIsPlaying(false);
            if (checkTimeIntervalRef.current) {
              clearInterval(checkTimeIntervalRef.current);
              checkTimeIntervalRef.current = null;
            }
          }
        }
      }, 100);
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (checkTimeIntervalRef.current) {
        clearInterval(checkTimeIntervalRef.current);
        checkTimeIntervalRef.current = null;
      }
    };
  }, []);

  const renderDesign = () => {
    switch (design) {
      case 'Design1':
      default:
        return (
          <div className="preview-spinning-disk-enhanced">
            <div 
              className="disk-overlay" 
              style={{ 
                transform: `rotate(${rotation}deg)`,
                backgroundImage: currentImage ? `url(${currentImage})` : 'none'
              }}
            >
              {!currentImage && (
                <div className="placeholder-circle">
                  <span>Upload Image</span>
                </div>
              )}
            </div>
          </div>
        );
      
      case 'Design2':
        return (
          <div className="preview-waveform-enhanced">
            {[...Array(16)].map((_, i) => (
              <div 
                key={i}
                className="wave-bar-enhanced"
                style={{
                  height: isPlaying 
                    ? `${30 + Math.sin((rotation / 10) + i) * 25}%` 
                    : '30%',
                  backgroundColor: diskColor
                }}
              />
            ))}
          </div>
        );

      case 'Design3':
        return (
          <div className="preview-grid-enhanced">
            {[...Array(4)].map((_, i) => (
              <div 
                key={i}
                className="grid-cell"
                style={{
                  backgroundImage: currentImage ? `url(${currentImage})` : 'none',
                  backgroundColor: diskColor,
                  transform: isPlaying 
                    ? `scale(${0.95 + Math.sin((rotation / 30) + i) * 0.05})` 
                    : 'scale(1)'
                }}
              />
            ))}
          </div>
        );
    }
  };

  return (
    <div className="enhanced-preview-container">
      <div className="preview-card">
        <div 
          className="preview-canvas" 
          style={{ backgroundColor: diskColor }}
        >
          {renderDesign()}
        </div>
        
        {showControls && (
          <div className="preview-controls">
            <button 
              className="play-button"
              onClick={togglePlayback}
              onTouchEnd={(e) => {
                if (!audioData?.file) return;
                e.preventDefault();
                e.stopPropagation();
                togglePlayback(e);
              }}
              disabled={!audioData?.file}
              type="button"
            >
              {isPlaying ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <rect x="6" y="4" width="3" height="12" />
                  <rect x="11" y="4" width="3" height="12" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <polygon points="6,4 6,16 14,10" />
                </svg>
              )}
            </button>
            <div className="preview-controls-info">
              <span className="preview-label">
                {isPlaying ? 'Playing Preview' : 'Click to Preview'}
              </span>
              <span className="preview-design">
                Design: {design === 'Design1' ? 'Spinning Vinyl' : 
                         design === 'Design2' ? 'Audio Wave' : 
                         'Album Grid'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedPreview;
